/** @file The inverse host part transform: a point pose moved, back in its own coordinates (22.3). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { hostShakeOf } from '@zilo/pose3d-render-core/place/hostShakeOf';
import type { PartTransform } from '@zilo/pose3d-pose/chain/partPointOf';
import { SHAKE_WAVE_DEFAULT } from '@zilo/pose3d-pose/chain/shakeWaveDefault';
import type { ShakeWave } from '@zilo/pose3d-pose/contract';

/**
 * `z = z' - sz`, `y = y' - ky z - sy`, `x = x' - shake(y', z') - kx z - sx`: undoes pose's `partPointOf` so a drawable
 * point can be built from the node's own (unmoved) coordinates and moved once (22.3 a); `p` itself when `t` is undefined.
 */
export function hostPartPointBackOf(p: Vec3, t: PartTransform | undefined, shakeClock: number, ppu: number,
  wave: ShakeWave = SHAKE_WAVE_DEFAULT): Vec3 {
  if (t === undefined) return p;
  const [sx, sy, sz] = t.shift ?? [0, 0, 0];
  const [kx, ky] = t.skew ?? [0, 0];
  const z = p[2] - sz;
  return [p[0] - hostShakeOf(p[1], p[2], t.shakePx ?? 0, shakeClock, ppu, wave) - kx * z - sx, p[1] - ky * z - sy, z];
}
